// src/services/aeo/clientDocsRules.ts
//
// The rules the server computes for documents (map_contract.md A.4), written
// as pure functions so the sample store can compute every state the way the
// backend will. Screens never call these to decide a state: the server sends
// states. The exceptions a screen may use are `parseDriveFolderLink` (to
// check a pasted link before sending it), `codeChipsOf` (to draw a doc's
// code chips with its outlet), `joinCodes` (to write codes "S1, S3"), and
// the catalogue's `activeDocExpected` / `expectedDocFor` (to name the doc that
// clears a code, and to offer a doc type in Pick a code).
//
// One clock: Malaysian time (UTC+8, no daylight saving). Every day count is
// in working days, skipping weekends and the fixed Malaysian holidays the
// backend knows (`_MY_FIXED_HOLIDAYS`, aeo_tracker.py:157). Movable holidays
// ship empty there until they are gazetted, so they are empty here too.

import type { DocAccount, DocSide, DocState, FileType, ThreadKind, ThreadState } from "./clientDocsTypes";
import type { ClientAnswerKind, QuestionKind } from "./clientDocsWriteTypes";

/* ------------------------------------------------------------------------- *
 * Dates, in Malaysian time
 * ------------------------------------------------------------------------- */

const MY_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 86_400_000;

/** Month and day of each fixed Malaysian public holiday, as the backend lists them. */
export const MY_FIXED_HOLIDAYS: ReadonlyArray<readonly [number, number]> = [
    [1, 1], // New Year's Day
    [2, 1], // Federal Territory Day
    [5, 1], // Labour Day
    [8, 31], // Merdeka Day
    [9, 16], // Malaysia Day
    [12, 25], // Christmas Day
];

/** The Malaysian calendar date of an instant, "YYYY-MM-DD". */
export function myDate(at: string | Date): string {
    const t = typeof at === "string" ? Date.parse(at) : at.getTime();
    return new Date(t + MY_OFFSET_MS).toISOString().slice(0, 10);
}

/** An instant for a Malaysian date and time: "2026-09-22", 10, 15 is 02:15 UTC. */
export function myInstant(ymd: string, hour = 10, minute = 0): string {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d, hour, minute) - MY_OFFSET_MS).toISOString();
}

function ymdToUtc(ymd: string): number {
    const [y, m, d] = ymd.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
}
function utcToYmd(t: number): string {
    return new Date(t).toISOString().slice(0, 10);
}

export function addDays(ymd: string, n: number): string {
    return utcToYmd(ymdToUtc(ymd) + n * DAY_MS);
}

/** The anniversary rule (`aeo_timeline.add_months`): the same day k months on, clamped to the month's end. */
export function addMonths(ymd: string, k: number): string {
    const [y, m, d] = ymd.split("-").map(Number);
    const total = m - 1 + k;
    const year = y + Math.floor(total / 12);
    const month = ((total % 12) + 12) % 12;
    const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return utcToYmd(Date.UTC(year, month, Math.min(d, last)));
}

export function isHoliday(ymd: string): boolean {
    const [, m, d] = ymd.split("-").map(Number);
    return MY_FIXED_HOLIDAYS.some(([hm, hd]) => hm === m && hd === d);
}

export function isWorkingDay(ymd: string): boolean {
    const dow = new Date(ymdToUtc(ymd)).getUTCDay();
    return dow !== 0 && dow !== 6 && !isHoliday(ymd);
}

/** `nth_working_day`: the nth working day counting `start` itself as day 1 when it works. */
export function nthWorkingDay(start: string, n: number): string {
    let d = start;
    let count = 0;
    for (let guard = 0; guard < 400; guard++) {
        if (isWorkingDay(d)) {
            count += 1;
            if (count === n) return d;
        }
        d = addDays(d, 1);
    }
    return d;
}

/** Working days after `from`, up to and including `to`. 0 when `to` is not after `from`. */
export function workingDaysBetween(from: string, to: string): number {
    if (to <= from) return 0;
    let n = 0;
    let d = addDays(from, 1);
    for (let guard = 0; guard < 800 && d <= to; guard++) {
        if (isWorkingDay(d)) n += 1;
        d = addDays(d, 1);
    }
    return n;
}

/** The date `n` working days before `today` (n > 0), or after it (n < 0). 0 is `today`. */
export function shiftWorkingDays(today: string, n: number): string {
    if (n === 0) return today;
    const step = n > 0 ? -1 : 1;
    let left = Math.abs(n);
    let d = today;
    for (let guard = 0; guard < 800 && left > 0; guard++) {
        d = addDays(d, step);
        if (isWorkingDay(d)) left -= 1;
    }
    return d;
}

/** `last_working_day_on_or_before`: `ymd` itself when it works, else the working day before it. */
export function lastWorkingDayOnOrBefore(ymd: string): string {
    let d = ymd;
    for (let guard = 0; guard < 30 && !isWorkingDay(d); guard++) d = addDays(d, -1);
    return d;
}

/** `next_monday_after`: the Monday after `ymd`. A Monday gives the next one, 7 days on. */
export function nextMondayAfter(ymd: string): string {
    const dow = new Date(ymdToUtc(ymd)).getUTCDay();
    const delta = (8 - dow) % 7;
    return addDays(ymd, delta || 7);
}

/** Rule 8: the 5th Malaysian working day after the Malaysian date of `sharedAt`. */
export function defaultReplyBy(sharedAt: string): string {
    return nthWorkingDay(addDays(myDate(sharedAt), 1), 5);
}

/** Which month of the term `today` falls in, by the anniversary rule. 1 before the start. */
export function termMonth(startedAt: string, today: string, termMonths: number): number {
    let k = 1;
    while (k < termMonths && addMonths(startedAt, k) <= today) k += 1;
    return k;
}

/** Day 1 of term month `m`. */
export function monthStart(startedAt: string, m: number): string {
    return addMonths(startedAt, m - 1);
}

/* ------------------------------------------------------------------------- *
 * Names and codes (rule 11)
 * ------------------------------------------------------------------------- */

/** A catalogue code, with an optional outlet: B1, S1 to S7, A1 to A4, G1 to G8, X1 upward. */
export const CODE_RE = /^(B1|S[1-7]|A[1-4]|G[1-8]|X\d+)(?:-([A-Z]{2,4}))?$/;
/** A code with no outlet. */
export const BASE_CODE_RE = /^(B1|S[1-7]|A[1-4]|G[1-8]|X\d+)$/;
/** A short outlet code, as a code's suffix carries it: 2 to 4 capitals, "MK". */
export const OUTLET_RE = /^[A-Z]{2,4}$/;

/**
 * An outlet written as its place name, "Mont Kiara". A capital first, then
 * letters, digits, spaces and the few marks a place name uses. NO underscore:
 * it would split the doc name, whose segments are underscore-separated.
 * Mirrors the backend's `oura_doc_names.outlet_ok` (#5254).
 */
export const PLACE_RE = /^[A-Z][A-Za-z0-9 '&.-]{0,39}$/;

/** An outlet is its place name, "Mont Kiara", or a short code, "MK". */
export function outletOk(outlet: string): boolean {
    return OUTLET_RE.test(outlet) || PLACE_RE.test(outlet);
}

/** Whether an outlet is short enough to ride on a code, "S2-MK". A place name is not. */
export function isShortOutlet(outlet: string | null | undefined): boolean {
    return !!outlet && OUTLET_RE.test(outlet);
}

/** The refusal, in the backend's own words, so the two never disagree. */
export const OUTLET_INVALID_TEXT = "An outlet is its place name, such as Mont Kiara, or 2 to 4 capital letters, such as MK.";

export interface ParsedCodes {
    /** Base codes, in the order written: ["S1","S3","B1","G4"]. */
    codes: string[];
    /** The first outlet written: "MK" from "S2-MK". */
    outlet: string | null;
    /** The code that carried the outlet. */
    outletCode: string | null;
}

/**
 * How Oura writes a list of codes in a name or a page title: a comma and a
 * space, "S1, S3, B1, G4" (YW, 29 Sep 2026).
 */
export const CODE_SEPARATOR = ", ";

/** Code labels as Oura writes them: ["S1","S2-MK"] reads "S1, S2-MK". */
export function joinCodes(labels: readonly string[]): string {
    return labels.join(CODE_SEPARATOR);
}

/** The distinct outlets written in one bracket group, in order: ["MK", "BB"] from "(S2-MK, S2-BB)". */
export function outletsInGroup(group: string): string[] {
    const out: string[] = [];
    for (const w of group.replace(/^\(|\)$/g, "").split(/[\s,]+/).filter(Boolean)) {
        const o = CODE_RE.exec(w)?.[2];
        if (o && !out.includes(o)) out.push(o);
    }
    return out;
}

/**
 * Codes from one bracket group, or null unless every item in it is a code.
 * Items are split on commas, spaces or both, so the decided "(S1, S3)", the
 * unspaced "(S1,S3)" and the older "(S1 S3)" all read as S1 and S3.
 *
 * THE HYPHEN IS TRIED SECOND, never first. A client doc's name now joins its
 * codes with one, "(S2-S4)", but the house format's own code can carry an
 * outlet suffix with one too, "(S2-MK)". A hyphen group is therefore read
 * whole first, where "S2-MK" is S2 at outlet MK; only when that fails is the
 * group split on hyphens and read again, where "S2-S4" is S2 and S4. An
 * outlet is 2 to 4 letters and a code always carries a digit, so the two can
 * never be confused.
 *
 * A doc carries 1 outlet (1 GBP Pack per outlet), so a group
 * naming 2 outlets, "(S2-MK, S2-BB)", does not read as codes: reading only
 * the first would leave the second outlet owed with no flag. The name then
 * carries no code, and the no_code flag says why (`unreadCodeGroup`).
 */
export function codesFromGroup(group: string): ParsedCodes | null {
    return readCodeWords(group.split(/[\s,]+/).filter(Boolean)) ?? readCodeWords(group.split(/[\s,-]+/).filter(Boolean));
}

/** Every word is a code, or null. The outlet is the first one written. */
function readCodeWords(words: readonly string[]): ParsedCodes | null {
    if (words.length === 0) return null;
    const out: ParsedCodes = { codes: [], outlet: null, outletCode: null };
    for (const w of words) {
        const m = CODE_RE.exec(w);
        if (!m) return null;
        if (!out.codes.includes(m[1])) out.codes.push(m[1]);
        if (m[2] && !out.outlet) {
            out.outlet = m[2];
            out.outletCode = m[1];
        } else if (m[2] && m[2] !== out.outlet) {
            return null;
        }
    }
    return out;
}

/** The first bracket group in `text` whose words are all codes. */
export function codesInText(text: string | null | undefined): ParsedCodes | null {
    if (!text) return null;
    const re = /\(([^()]*)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
        const got = codesFromGroup(m[1]);
        if (got) return got;
    }
    return null;
}

/**
 * The first bracket group in a file name that looks like it holds a code but
 * does not read as one, as written: "(S9)", "(s1)", "(S1, S9)", or 2 outlets
 * in 1 doc, "(S2-MK, S2-BB)". Null when every group reads, or none looks like
 * a code. A version such as "(v2)" is not a code. For the no_code flag, so it
 * can say why the name gave no code.
 */
export function unreadCodeGroup(name: string | null | undefined): string | null {
    if (!name) return null;
    const re = /\(([^()]*)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(name))) {
        if (/\b(?![vV]\d)[A-Za-z]\d+\b/.test(m[1]) && !codesFromGroup(m[1])) return `(${m[1].trim()})`;
    }
    return null;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const NAME_DATE_RE = /^(\d{1,2}) ([A-Za-z]+) (\d{4})$/;
const NAME_VERSION_RE = /^[vV](\d+(?:\.\d+)?)$/;

/** "18 Sep 2026", "18 September 2026" or the new name's "06 October 2026" to an ISO day. */
export function parseNameDate(s: string): string | null {
    const m = NAME_DATE_RE.exec(s.trim());
    if (!m) return null;
    const month = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    if (month < 0) return null;
    const day = Number(m[1]);
    if (day < 1 || day > 31) return null;
    return `${m[3]}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export interface ParsedName extends ParsedCodes {
    docType: string | null;
    /**
     * The new name's own `<Location>` segment, the outlet's place in words
     * ("Mont Kiara"). Null for a name that has none, and for both older
     * formats, which wrote the place inside the doc words instead ("GBP Pack
     * Mont Kiara"). It is NOT the outlet code: a client doc's codes no longer
     * carry one, so a new name gives the place and the document record (the
     * server's own `outlet`) gives the code.
     */
    location: string | null;
    version: string | null;
    date: string | null;
    /**
     * Which name this read as: `oura` the name Oura writes now,
     * "(S2-S4) The Lush Clinic_GBP Pack_Mont Kiara_06 October 2026" (with a
     * trailing "_1.0" on a doc made before #6803); `house` the
     * one it used to write, "ourteam_…_v1.0_6 Oct 2026"; `legacy` a name the
     * team wrote by hand that still carries its codes somewhere. Null when no
     * code reads at all.
     */
    format: "oura" | "house" | "legacy" | null;
}

const EXTENSION_RE = /\.(docx?|pdf|xlsx?|csv|txt|pptx?)$/i;
/** The house "sent" suffix: "_s26 Sep 2026" after the date. */
const SENT_SUFFIX_RE = /_s(\d{1,2} [A-Za-z]+ \d{4})$/;

/** The new name's leading codes and client: "(S2-S4) The Lush Clinic". */
const OURA_LEAD_RE = /^\(([^()]*)\)\s+(\S.*)$/;
/** Its version segment: "1.0", and "v1.0" from a name written before the `v` went. */
const OURA_VERSION_RE = /^[vV]?(\d+(?:\.\d+)?)$/;

/**
 * `parse_name`, over all three names a client doc can carry:
 *
 * 1. `oura`, what Oura writes now (B16, #5254):
 *    `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>_<ver>`, for example
 *    `(S2-S4) The Lush Clinic_GBP Pack_Mont Kiara_06 October 2026_1.0`. The
 *    codes lead, joined with hyphens and carrying no outlet; the Location
 *    segment is the outlet's place and is left out when there is none.
 * 2. `house`, what it wrote before:
 *    `ourteam_<Client>_<Doc> (<codes>)_v<ver>_<D Mon YYYY>`.
 * 3. `legacy`, a hand-written name, codes read from any bracket group whose
 *    items are all codes: `W1 (S1) The Lush Clinic_Answer Plan_18 September 2026_V1`.
 *
 * NOTHING ALREADY FILED LOSES ITS CODES. The new pattern is read first, but
 * both older names keep reading exactly as before, so a doc in a client's
 * folder keeps its codes, outlet, version and date without being renamed.
 * Commas, spaces, hyphens or a mix separate the codes, so names written before
 * the comma rule ("(S1 S3 B1 G4)") still read. A trailing sent suffix
 * ("_s26 Sep 2026") is dropped first.
 *
 * A name shaped like the new one whose leading group is NOT codes, such as
 * `(Draft) Client_Answer Plan (S1)_18 September 2026_1.0`, does not stop
 * there: its codes are looked for in the rest of the name, the way a legacy
 * name's are (Jack, #3972). Returning early on "(Draft)" would have taken S1
 * off a doc that has it written two segments later.
 */
export function parseDocName(rawName: string): ParsedName {
    const bare = rawName.trim().replace(EXTENSION_RE, "");
    const sent = SENT_SUFFIX_RE.exec(bare);
    const name = sent && parseNameDate(sent[1]) ? bare.slice(0, sent.index) : bare;
    const parts = name.split("_");
    const dateIdx = parts.findIndex((p) => parseNameDate(p) !== null);
    const versionIdx = parts.findIndex((p) => NAME_VERSION_RE.test(p.trim()));
    const date = dateIdx >= 0 ? parseNameDate(parts[dateIdx]) : null;
    const versionMatch = versionIdx >= 0 ? NAME_VERSION_RE.exec(parts[versionIdx].trim()) : null;
    const version = versionMatch ? versionMatch[1] : null;

    // The doc segment: the one before the version or the date, whichever comes first.
    const tailIdx = [versionIdx, dateIdx].filter((i) => i > 0).sort((a, b) => a - b)[0] ?? -1;
    const docSeg = tailIdx > 1 || (tailIdx === 1 && parts[0].toLowerCase() !== "ourteam") ? parts[tailIdx - 1] : null;
    const docType = docSeg ? docSeg.replace(/\([^()]*\)/g, "").replace(/\s+/g, " ").trim() || null : null;

    const none: ParsedName = { codes: [], outlet: null, outletCode: null, docType, location: null, version, date, format: null };

    // 1. The name Oura writes now, read by its segments rather than by hunting
    //    for the date and version: the Location segment sits between the doc
    //    and the date, so "Mont Kiara" must not be mistaken for the doc type.
    //    Since #6803 the name ends on its date; a doc made before still ends on
    //    its version. The last segment tells the two apart.
    const lead = OURA_LEAD_RE.exec(parts[0]);
    const ver = lead && parts.length >= 4 ? OURA_VERSION_RE.exec(parts[parts.length - 1].trim()) : null;
    const dayAt = ver ? parts.length - 2 : parts.length - 1;
    const segs = dayAt;                                     // client, doc and an optional place before the date
    if (lead && (segs === 2 || segs === 3)) {
        const day = parseNameDate(parts[dayAt]);
        if (day) {
            // The doc segment without any bracket group of its own, so
            // "Answer Plan (S1)" in a hand-written name reads as the type.
            const type = parts[1].replace(/\([^()]*\)/g, "").replace(/\s+/g, " ").trim() || null;
            const place = segs === 3 ? parts[2].trim() || null : null;
            const read = { docType: type, location: place, version: ver ? ver[1] : null, date: day };
            const got = codesFromGroup(lead[1]);
            if (got) return { ...got, ...read, format: "oura" as const };
            // The lead is not codes. This is a hand-written name that happens
            // to be shaped like the new one, so its codes are read from
            // wherever they are written, and it reads as what it is: legacy.
            const anywhere = codesInText(name);
            if (anywhere) return { ...anywhere, ...read, format: "legacy" as const };
            return { codes: [], outlet: null, outletCode: null, ...read, format: null };
        }
    }

    const house = parts[0] === "ourteam" && parts.length >= 5 && versionIdx === parts.length - 2 && dateIdx === parts.length - 1;
    if (house) {
        const got = codesInText(parts[parts.length - 3]);
        if (got) return { ...got, docType, location: null, version, date, format: "house" };
        return none;
    }
    const got = codesInText(name);
    if (got) return { ...got, docType, location: null, version, date, format: "legacy" };
    return none;
}

/** Codes from the page title (line 1), the backup when the name carries none. */
export function parseTitleCodes(pageTitle: string | null | undefined): ParsedCodes | null {
    if (!pageTitle) return null;
    return codesInText(pageTitle.split("\n")[0]);
}

/** A thread's "[S2-MK]" prefix, as written, or null. */
export function threadCodePrefix(text: string): string | null {
    const m = /^\s*\[([^\]]+)\]/.exec(text);
    if (!m) return null;
    return CODE_RE.test(m[1].trim()) ? m[1].trim() : null;
}

/**
 * Pairs each of a doc's codes with its outlet, for chips such as "S2-MK".
 * The contract carries one outlet per doc and not which code it belongs to,
 * so it goes on S2 (the per-outlet code) when the doc has S2, otherwise on the
 * first code.
 *
 * A PLACE-NAME outlet does not ride on the code: "S2-Mont Kiara" would not
 * read back as a code, and the backend's `title_of` writes plain codes for it
 * ("GBP Pack, Mont Kiara (S2, S4)"). The chip still CARRIES the outlet, so a
 * board row and a thread stay that outlet's — only the label stays plain.
 */
export function codeChipsOf(codes: readonly string[], outlet: string | null): Array<{ code: string; outlet: string | null; label: string }> {
    const host = outlet ? (codes.includes("S2") ? "S2" : codes[0] ?? null) : null;
    return codes.map((code) => {
        const o = code === host ? outlet : null;
        return { code, outlet: o, label: isShortOutlet(o) ? `${code}-${o}` : code };
    });
}

/* ------------------------------------------------------------------------- *
 * Folder links
 * ------------------------------------------------------------------------- */

export type FolderLinkCheck = { ok: true; folderId: string; url: string } | { ok: false; message: string };

const DRIVE_ID_RE = /^[A-Za-z0-9_-]{10,}$/;

/**
 * Reads a pasted Drive folder link: `/drive/folders/<id>`, `?id=<id>`, or a
 * bare id. Spaces around it are trimmed. Says what is wrong when it isn't one.
 */
export function parseDriveFolderLink(raw: string): FolderLinkCheck {
    const text = (raw ?? "").trim();
    if (!text) return { ok: false, message: "Paste the folder's link." };
    if (DRIVE_ID_RE.test(text)) return { ok: true, folderId: text, url: `https://drive.google.com/drive/folders/${text}` };
    let u: URL;
    try {
        u = new URL(text);
    } catch {
        return { ok: false, message: "That isn't a Google Drive folder link." };
    }
    const host = u.hostname.toLowerCase();
    if (host === "docs.google.com") {
        return { ok: false, message: "That's a link to a document. Paste the link of the folder it sits in." };
    }
    if (host !== "drive.google.com") return { ok: false, message: "That isn't a Google Drive folder link." };
    const fromPath = /\/folders\/([A-Za-z0-9_-]{10,})/.exec(u.pathname);
    const fromQuery = u.searchParams.get("id");
    if (/\/file\/d\//.test(u.pathname)) {
        return { ok: false, message: "That's a link to a file. Paste the link of the folder it sits in." };
    }
    const id = fromPath?.[1] ?? (fromQuery && DRIVE_ID_RE.test(fromQuery) ? fromQuery : null);
    if (!id) return { ok: false, message: "That isn't a Google Drive folder link." };
    return { ok: true, folderId: id, url: `https://drive.google.com/drive/folders/${id}` };
}

/* ------------------------------------------------------------------------- *
 * Threads (rules 1 and 2)
 * ------------------------------------------------------------------------- */

/** Domains whose accounts are ours (`OURA_OWN_DOMAINS`). */
export const OWN_DOMAINS = ["ourteam.ai"];
/** The account Oura reads and posts as (`OURA_DRIVE_SUBJECT`). */
export const OURA_ACCOUNT = "oura@ourteam.ai";
/** The old posting account, before the switch to oura@. */
export const NO_REPLY_ACCOUNT = "no-reply@ourteam.ai";

export function isOurEmail(email: string | null | undefined): boolean {
    if (!email) return false;
    const domain = email.toLowerCase().split("@")[1] ?? "";
    return OWN_DOMAINS.includes(domain);
}

/** Rule 1: which side and which account a post came from. */
export function whoSpoke(email: string | null | undefined): { side: DocSide; account: DocAccount } {
    const e = (email ?? "").toLowerCase();
    if (!isOurEmail(e)) return { side: "client", account: "client" };
    if (e === OURA_ACCOUNT) return { side: "us", account: "oura" };
    if (e === NO_REPLY_ACCOUNT) return { side: "us", account: "no_reply" };
    return { side: "us", account: "staff" };
}

/**
 * B26: who a post speaks for. A post oura@ wrote for the client (its id is in
 * Oura's on-behalf record) counts as the client's: side "client", account
 * "oura", `onBehalfOf` the client's name. Every other post: rule 1.
 */
export function whoSpokeFor(email: string | null | undefined, onBehalfOf: string | null | undefined): { side: DocSide; account: DocAccount; onBehalfOf: string | null } {
    if (onBehalfOf) return { side: "client", account: "oura", onBehalfOf };
    return { ...whoSpoke(email), onBehalfOf: null };
}

const PLACEHOLDER_RE = /leave any comments|comments or suggestions|feel free to comment/i;
const QUESTION_START_RE = /^(please|kindly|can|could|would|do|is|are|confirm|approve|let us know|tell us)\b/i;

/** Rule 2: the kind of a thread, from who opened it and what it says. */
export function threadKind(openedBySide: DocSide, text: string): ThreadKind {
    if (openedBySide === "client") return "raised_by_client";
    const body = text.replace(/^\s*\[[^\]]*\]\s*/, "").trim();
    if (PLACEHOLDER_RE.test(body)) return "placeholder";
    if (body.includes("?") || QUESTION_START_RE.test(body) || /reply agreed/i.test(body)) return "question";
    return "our_note";
}

/* ------------------------------------------------------------------------- *
 * Files
 * ------------------------------------------------------------------------- */

const MIME_TYPE: Record<string, FileType> = {
    "application/vnd.google-apps.document": "gdoc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
    "application/msword": "word",
    "application/pdf": "pdf",
    "application/vnd.google-apps.spreadsheet": "sheet",
};

/** `fileType` from a Drive mimeType. */
export function fileTypeOf(mimeType: string): FileType {
    return MIME_TYPE[mimeType] ?? "other";
}

/* ------------------------------------------------------------------------- *
 * Which codes go through a client document, and when (B5)
 * ------------------------------------------------------------------------- */

/**
 * The docs a client reviews, a catalogue fact (YW, 29 Sep 2026): which codes
 * go through a client document. WHEN is not here. It comes from the plan: the
 * month the engagement puts the code in (`deliverables[].months`), and that
 * month's window on Oura's timeline. The timeline and the Execution Timeline
 * date each deliverable to a month, never a week. This replaces the old map
 * of fixed weeks.
 */
export interface DocExpectation {
    docType: string;
    codes: string[];
    /** S2 and S4: 1 GBP Pack per outlet. */
    perOutlet?: boolean;
    /** G1 to G8: a new Monthly Performance Report or Monthly Work Snapshot in each plan month the engagement lists the code in. */
    eachMonth?: boolean;
}

/**
 * The catalogue's doc types, in the team's own words, mirroring the backend's `DOC_TYPES`
 * (ourteam-backend#5254): the names on the templates in the shared folder, so
 * a template is found by the codes at the front of its file name and the doc
 * it makes is called the same thing on every screen.
 *
 * S4 rides in the GBP Pack, not a type of its own: that is how the team
 * already works, and offering "Business details" asked Create for a template
 * nobody keeps — a 409 every time.
 *
 * Each month the team sends two docs (ourteam-backend#6868): the Monthly
 * Performance Report, the month's results (G2, G3, G4, G7: AI Check, Site
 * Check, Scoreboard, Competitor Watch), and the Monthly Work Snapshot, the
 * month's work (G1, G5, G6, G8). They replace the Monthly Update, which
 * covered G1 to G8 in one. G4 is in both the Answer Plan and the Performance
 * Report on purpose: the plan sets the watch up once and each month reports
 * on it. `expectedDocFor` takes the first, so G4's own row names the Answer Plan.
 *
 * OLD DOCS STILL READ. A doc already named "Google profile pack (S2-MK)",
 * "Business details (S4)" or "Monthly Update" keeps its name and keeps covering its codes: the
 * board places a document by the CODE row the backend attached it to, never by
 * the words in its title. Nothing filed is renamed or re-coded.
 */
export const DOC_EXPECTED_CURRENT: readonly DocExpectation[] = [
    // B1 is the Copy Pack's alone and S3 the Technical Crawl's (ourteam-backend#6394): a new Answer Plan
    // offers neither. One named "(S1, S3, B1, G4)" before keeps covering its codes on the board.
    { docType: "Answer Plan", codes: ["S1", "G4"] },
    { docType: "GBP Pack", codes: ["S2", "S4"], perOutlet: true },
    // What staff changed on each profile once applied (ourteam-backend#6394). The GBP Pack's codes,
    // after it on purpose: `expectedDocFor` takes the first, so S2 and S4 still name the GBP Pack.
    { docType: "GBP Changes", codes: ["S2", "S4"], perOutlet: true },
    { docType: "Copy Pack", codes: ["B1"] },
    // The technical fixes the crawl found (ourteam-backend#6394): a copy of its Drive template, no skill yet.
    { docType: "Technical Crawl", codes: ["S3"] },
    { docType: "Monthly Performance Report", codes: ["G2", "G3", "G4", "G7"], eachMonth: true },
    { docType: "Monthly Work Snapshot", codes: ["G1", "G5", "G6", "G8"], eachMonth: true },
];

/**
 * The catalogue before Technical Crawl (FE main, up to #4623), which the backend serves until
 * ourteam-backend#6394 is deployed. S3 keeps its old place under the Answer Plan here.
 */
export const DOC_EXPECTED_BEFORE_TECHNICAL_CRAWL: readonly DocExpectation[] = [
    { docType: "Answer Plan", codes: ["S1", "S3", "G4"] },
    { docType: "GBP Pack", codes: ["S2", "S4"], perOutlet: true },
    { docType: "GBP Changes", codes: ["S2", "S4"], perOutlet: true },
    { docType: "Copy Pack", codes: ["B1"] },
    { docType: "Monthly Performance Report", codes: ["G2", "G3", "G4", "G7"], eachMonth: true },
    { docType: "Monthly Work Snapshot", codes: ["G1", "G5", "G6", "G8"], eachMonth: true },
];

/*
 * Which catalogue applies, from the backend's own list (ourteam-backend#6394). The doc templates
 * response lists Technical Crawl once the backend serves it; until a read says so, the catalogue is
 * the one before it, so a release in either order never offers a doc type the backend refuses.
 */
const TECHNICAL_CRAWL = "Technical Crawl";
let servesTechnicalCrawl = false;
const catalogueListeners = new Set<() => void>();

/** The catalogue in force now: the one the backend's templates list is for. */
export function activeDocExpected(): readonly DocExpectation[] {
    return servesTechnicalCrawl ? DOC_EXPECTED_CURRENT : DOC_EXPECTED_BEFORE_TECHNICAL_CRAWL;
}

/** Record the doc types the backend's templates list names; a change in the catalogue tells its listeners. */
export function setServedDocTypes(docTypes: readonly string[]): void {
    const before = activeDocExpected();
    servesTechnicalCrawl = docTypes.includes(TECHNICAL_CRAWL);
    if (activeDocExpected() !== before) catalogueListeners.forEach((listen) => listen());
}

/** Subscribe to changes of the active catalogue (for `useSyncExternalStore`). */
export function subscribeDocExpected(listen: () => void): () => void {
    catalogueListeners.add(listen);
    return () => {
        catalogueListeners.delete(listen);
    };
}

/** Doc types retired from the catalogue: their docs stay, a custom type cannot take the name (ourteam-backend#6868). */
const RETIRED_TYPES: Readonly<Record<string, readonly string[]>> = {
    "monthly update": ["Monthly Performance Report", "Monthly Work Snapshot"],
};

/** Whether a doc type is made new each plan month and named with its month, "Month 3" (ourteam-backend#6868). */
export function isMonthlyType(docType: string | null | undefined): boolean {
    return activeDocExpected().some((e) => e.eachMonth && e.docType === docType);
}

/** A monthly doc's `<Location>` in its file name, "Month 3"; null without a month. */
export function monthPart(month: number | null | undefined): string | null {
    return month ? `Month ${month}` : null;
}

/** The plan month a monthly doc's name already gives ("…_Month 3_…"), or null. */
export function nameMonth(name: string | null | undefined): number | null {
    const m = /^Month (\d{1,2})$/.exec(parseDocName(name ?? "").location?.trim() ?? "");
    return m ? Number(m[1]) : null;
}

/* ------------------------------------------------------------------------- *
 * A custom doc type (ourteam-backend#6516): staff name it themselves
 * ------------------------------------------------------------------------- */

export const CUSTOM_LABEL_MIN = 2;
export const CUSTOM_LABEL_MAX = 40;

/** A custom label as the backend stores it: trimmed, inner spaces collapsed. */
export function customLabel(label: string): string {
    return label.trim().replace(/\s+/g, " ");
}

/**
 * Why a custom doc type's name cannot be used, in the backend's words; null when it
 * can. The backend's `custom_label_problem` is the rule and refuses on its own; this
 * mirror only says so before the press. Never a catalogue type nor containing one,
 * inside a word too: "Copy Pack Menu" and "Answer Planner" would read as those types.
 */
export function customLabelProblem(label: string): string | null {
    const text = customLabel(label);
    if (text.length < CUSTOM_LABEL_MIN || text.length > CUSTOM_LABEL_MAX) {
        return `A doc type name is ${CUSTOM_LABEL_MIN} to ${CUSTOM_LABEL_MAX} characters.`;
    }
    if (/[_()]/.test(text)) return "A doc type name has no underscore or brackets.";
    if (!/^[A-Za-z0-9 &'.,-]+$/.test(text) || !/[A-Za-z]/.test(text)) return "A doc type name uses letters, digits, spaces and & ' - . , only.";
    const low = text.toLowerCase();
    // Every canonical name of the new catalogue is reserved, whichever one is active: a custom type
    // named "Technical Crawl" would collide with the built-in type once the backend serves it.
    for (const e of [...DOC_EXPECTED_CURRENT, ...activeDocExpected()]) {
        const name = e.docType.toLowerCase();
        if (low === name) return `${e.docType} is one of Oura's doc types already. Pick it from the list.`;
        if (low.includes(name)) return `A doc type name cannot contain ${e.docType}: Oura would read it as that doc type.`;
    }
    for (const [name, instead] of Object.entries(RETIRED_TYPES)) {
        const title = name.replace(/\b\w/g, (c) => c.toUpperCase());
        if (low.includes(name)) return `The ${title} is retired: pick the ${instead.join(" or the ")}.`;
    }
    return null;
}

/** Whether a code goes through a client document at all. */
export function expectsClientDoc(code: string): boolean {
    return activeDocExpected().some((e) => e.codes.includes(code));
}

/**
 * The document that clears a code: the first catalogue doc that covers it
 * (B1 is the Copy Pack's, ourteam-backend#6394). Null for a code no
 * client document covers. The board names the doc to send with it.
 */
export function expectedDocFor(code: string): DocExpectation | null {
    return activeDocExpected().find((e) => e.codes.includes(code)) ?? null;
}

/** Whether a code wants a new doc in each plan month it is in (G1 to G8, in the two monthly docs). */
export function expectedEachMonth(code: string): boolean {
    return activeDocExpected().some((e) => e.eachMonth && e.codes.includes(code));
}

/** The Foundation build, plan month 1: 20 working days from Day 1 to handover (`aeo_timeline.BUILD_WORKING_DAYS`). */
export const BUILD_WORKING_DAYS = 20;

/**
 * Day 1 of Oura's timeline for an engagement start date: `clock_start`, the
 * Monday after it, as `build_schedule` reads `trigger_date`. A published
 * timeline carries its own `clock_start`, which wins.
 */
export function planDay1(startedAt: string): string {
    return nextMondayAfter(startedAt);
}

export interface PlanWindow {
    /** The plan month; 1 is the Foundation build. */
    month: number;
    /** YYYY-MM-DD */
    start: string;
    /** YYYY-MM-DD, the window's last day. */
    end: string;
    /** The window's last working day: a doc due in it is late after this. */
    lastWorkingDay: string;
}

/**
 * Plan month `month`'s window on Oura's timeline, as `build_schedule` draws
 * it. Month 1 is the Foundation build, from Day 1 to handover, the 20th
 * working day counting Day 1. Month k runs from add_months(Day 1, k - 1) to
 * the day before add_months(Day 1, k), and its last working day is that
 * month's report date.
 */
export function planWindow(day1: string, month: number): PlanWindow {
    if (month <= 1) {
        const handover = nthWorkingDay(day1, BUILD_WORKING_DAYS);
        return { month: 1, start: day1, end: handover, lastWorkingDay: handover };
    }
    const start = addMonths(day1, month - 1);
    const end = addDays(addMonths(day1, month), -1);
    return { month, start, end, lastWorkingDay: lastWorkingDayOnOrBefore(end) };
}

export interface CodeExpectation {
    /** The plan month whose window the owed doc falls in. */
    month: number;
    /** That window's first day: "Not sent yet" from here. */
    from: string;
    /** That window's last working day: `CodeRow.expectedBy`. */
    by: string;
    /** Docs owed so far: 1, or for a monthly code 1 per month started (at least 1). */
    docs: number;
    /** The shared docs that count toward it. */
    shared: number;
    /** The window has started and the doc owed is not shared with the client. */
    notSentYet: boolean;
    /** Not sent yet, and the window's last working day has passed: "Was due". */
    late: boolean;
}

/**
 * When a code's client doc is due, and whether it is owed now (B5). Null when
 * the code goes through no client doc, the engagement lists no month for it,
 * or the plan has no Day 1.
 *
 * `sharedOn` holds, for each doc covering the code that the client can see,
 * the Malaysian date Oura first saw it shared, or null when that is unknown.
 * - A once-only code is due in its first plan month. Any shared doc covers it.
 * - A monthly code (G1) owes 1 doc for each of its months whose window has
 *   started. Docs shared on or after its first month's first day count, and
 *   cover its months in order, so the month due is the first not covered. A
 *   shared doc whose share date is unknown counts, so Oura never says "Not
 *   sent yet" about a doc the client can open.
 */
export function codeExpectation(
    code: string,
    months: readonly number[],
    day1: string | null,
    today: string,
    sharedOn: readonly (string | null)[] = [],
): CodeExpectation | null {
    if (!day1 || !expectsClientDoc(code)) return null;
    const ms = Array.from(new Set(months.filter((m) => Number.isInteger(m) && m >= 1))).sort((a, b) => a - b);
    if (ms.length === 0) return null;
    if (!expectedEachMonth(code)) {
        const w = planWindow(day1, ms[0]);
        const shared = sharedOn.length;
        const notSentYet = shared === 0 && today >= w.start;
        return { month: w.month, from: w.start, by: w.lastWorkingDay, docs: 1, shared, notSentYet, late: notSentYet && today > w.lastWorkingDay };
    }
    const windows = ms.map((m) => planWindow(day1, m));
    const shared = sharedOn.filter((d) => d === null || d >= windows[0].start).length;
    const started = windows.filter((w) => w.start <= today).length;
    const notSentYet = shared < started;
    const w = windows[Math.min(shared, windows.length - 1)];
    return { month: w.month, from: w.start, by: w.lastWorkingDay, docs: Math.max(1, started), shared, notSentYet, late: notSentYet && today > w.lastWorkingDay };
}

/** The median of a list of numbers, rounded to a whole day; null for none. */
export function medianWhole(values: number[]): number | null {
    if (values.length === 0) return null;
    const s = [...values].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    const m = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
    return Math.round(m);
}

/* ------------------------------------------------------------------------- *
 * B26: agreement, each code's own questions, and the amber flag
 * ------------------------------------------------------------------------- */

/**
 * Words that agree, at the start of a client's post: "Yes", "Agreed",
 * "Approved, thank you", "OK", "Confirmed", "Looks good", "Go ahead". A post
 * that agrees but asks for a change ("Can it say 3 courses? Otherwise
 * AGREED.") does not start with them, so it is not an agreement.
 */
export const AGREEMENT_RE = /^\s*(yes|yep|agreed?|approved?|ok(ay)?|confirmed?|looks good|go ahead|all good)\b/i;

/**
 * Words after the yes that ask for something else: "Yes, but change the
 * price to RM80", "Approved, except the hours", "Looks good. Could you add
 * the Sunday times?". The yes is about the rest; the change is still open.
 */
const CHANGE_AFTER_AGREEMENT_RE = /\b(?:but|however|except|although|instead|rather|change|update|replace|remove|add|can you|could you|would you)\b/i;

/** Whether a client's post opens with words that agree, whatever follows them. */
export function opensWithAgreement(text: string | null | undefined): boolean {
    return !!text && AGREEMENT_RE.test(text);
}

/**
 * Whether a client's words agree (B26). An empty post (a bare resolve) is not
 * words, and a yes followed by a change ("Yes, but change...") is not an
 * agreement: resolving it asks "Resolve anyway?", and it never counts as the
 * client's approval of wording they asked us to change.
 */
export function readsAsAgreement(text: string | null | undefined): boolean {
    if (!text || !opensWithAgreement(text)) return false;
    return !CHANGE_AFTER_AGREEMENT_RE.test(text.replace(AGREEMENT_RE, ""));
}

/** The client's last word on a thread, as the agreement rule reads it. */
export interface ClientWord {
    text: string;
    /** The answer kind, for a post on their behalf from Oura's answer screen. */
    answer?: ClientAnswerKind | null;
}

/**
 * B26: whether a resolve carries the client's agreement. True when the
 * client's side resolved it (in the doc, or by Agree in Oura), or when the
 * client's last word before our resolve was an agreement: an Agree answer,
 * words that `readsAsAgreement`, or, for a question that asks them to pick,
 * write or send something (choice, text, file), any answer but a Change.
 * False is the amber flag: we resolved it without their agreement.
 */
export function resolveCarriesAgreement(resolvedBySide: DocSide, lastClientWord: ClientWord | null, questionKind: QuestionKind | null = null): boolean {
    if (resolvedBySide === "client") return true;
    if (!lastClientWord) return false;
    if (lastClientWord.answer === "agree") return true;
    if (lastClientWord.answer === "change") return false;
    if (questionKind === "choice" || questionKind === "text" || questionKind === "file") return true;
    if (lastClientWord.answer) return false;
    return readsAsAgreement(lastClientWord.text);
}

/**
 * B26: whether a thread counts toward a code row. A thread with no "[S2-MK]"
 * prefix (and no code on its question) applies to every code of its doc; one
 * with a code applies only to that code, and only to that outlet when both
 * name one.
 */
export function threadCovers(threadCode: string | null | undefined, code: string, outlet: string | null): boolean {
    if (!threadCode) return true;
    const m = CODE_RE.exec(threadCode.trim());
    if (!m) return true;
    if (m[1] !== code) return false;
    return !m[2] || !outlet || m[2] === outlet;
}

/** A thread as rule 4 reads it. */
export interface StateThread {
    kind: ThreadKind;
    state: ThreadState | null;
    code: string | null;
}

/** Rule 4, first match wins: needs us, late, waiting on the client, approved, nothing asked. */
export function docStateOf(clientCanSee: boolean, read: boolean, threads: readonly StateThread[]): DocState | null {
    if (!clientCanSee) return "not_shared";
    if (!read) return null;
    const questions = threads.filter((t) => t.kind === "question");
    if (threads.some((t) => t.state === "needs_us")) return "needs_us";
    if (questions.some((t) => t.state === "late")) return "late";
    if (questions.some((t) => t.state === "waiting_on_client")) return "waiting_on_client";
    if (questions.length > 0) return "approved";
    return "nothing_asked";
}

/**
 * B26: a doc's state for one of its codes, from that code's own questions
 * (rule 4 over the threads that cover it), so an Answer Plan whose S1
 * questions are all approved reads Approved on S1 while S3 still waits.
 */
export function docStateForCode(clientCanSee: boolean, read: boolean, threads: readonly StateThread[], code: string, outlet: string | null): DocState | null {
    return docStateOf(clientCanSee, read, threads.filter((t) => threadCovers(t.code, code, outlet)));
}

/* ------------------------------------------------------------------------- *
 * B16: the name Oura writes
 * ------------------------------------------------------------------------- */

const NAME_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** The house date: "2026-10-02" to "2 Oct 2026", no leading zero. Still read, no longer written. */
export function nameDateOf(ymd: string): string {
    const [y, m, d] = ymd.split("-").map(Number);
    return `${d} ${NAME_MONTHS[m - 1]} ${y}`;
}

/**
 * The date in a client doc's name: "2026-10-06" to "06 October 2026" — two
 * digits for the day, the month in full English, no slashes (the team's
 * decision, 6 Oct 2026, so a downloaded file keeps the name). The day is
 * Malaysian, because the date passed in is (`myToday`).
 */
export function docDateOf(ymd: string): string {
    const [y, m, d] = ymd.split("-").map(Number);
    return `${String(d).padStart(2, "0")} ${MONTHS_LONG[m - 1]} ${y}`;
}

/** The next minor version: "1.0" to "1.1", "1.9" to "1.10", "2" to "2.1". */
export function nextVersion(version: string | null | undefined): string {
    const [major, minor] = (version ?? "1.0").split(".").map((x) => Number(x) || 0);
    return `${major || 1}.${(minor ?? 0) + 1}`;
}

export interface DocNameParts {
    client: string;
    docType: string;
    /**
     * Bare codes, no outlet suffix: ["S1","S3","B1","G4"], ["S2","S4"]. The
     * outlet is the `location`, not part of a code (the team's decision,
     * 6 Oct 2026).
     */
    codes: readonly string[];
    /**
     * The outlet's place in words, "Mont Kiara": its own segment in the name.
     * Left out when there is none.
     *
     * STILL OPEN with the team: what Location a doc that is NOT per outlet
     * carries for a client with 2 or more outlets (the business's main area,
     * the outlet the doc is for, or nothing). Leaving it out is the option
     * that invents nothing, so a doc with no place passes none and the name
     * closes on the date and version.
     */
    location?: string | null;
    /**
     * Only for a doc made before versions were dropped (#6803), renamed in its own
     * format. A new doc has none, and its name ends on the date.
     */
    version?: string | null;
    /** YYYY-MM-DD */
    date: string;
}

/** The codes as a doc name writes them: "(S2-S4)", hyphens, no spaces. */
export function nameCodeGroup(codes: readonly string[]): string {
    return `(${codes.join("-")})`;
}

/**
 * The name Oura writes for a client doc (B16, #5254):
 * `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>_<ver>`, for example
 * `(S2-S4) The Lush Clinic_GBP Pack_Mont Kiara_06 October 2026_1.0`. The same
 * string the backend's name builder writes, so Create, Rename, the filed PDF
 * and the drawer's preview never disagree.
 *
 * No version since #6803 (the product owner, 1 Nov 2026): the name ends on the
 * date and line 2 is empty (`lineTwoOf`). A doc made before keeps its version
 * ("…_1.0"), passed in as `version`. Underscores inside the client, doc or place words would split
 * the name, so they become spaces. A doc with no code gets no leading group
 * rather than an empty "()", and one with no place gets no Location segment
 * rather than an empty one.
 */
export function docNameOf(p: DocNameParts): string {
    const clean = (x: string) => x.replace(/_/g, " ").replace(/\s+/g, " ").trim();
    const codes = p.codes.length ? `${nameCodeGroup(p.codes)} ` : "";
    const place = p.location?.trim() ? `${clean(p.location)}_` : "";
    const tail = p.version ? `_${p.version}` : "";
    return `${codes}${clean(p.client)}_${clean(p.docType)}_${place}${docDateOf(p.date)}${tail}`;
}

/** The doc's version line, line 2: "Nyonya Table · v1.1". */
export function versionLineOf(client: string, version: string): string {
    return `${client} · v${version}`;
}

/**
 * What line 2 holds: nothing on a doc with no version (#6803), the version
 * line on a doc made before versions were dropped. The paragraph stays, empty,
 * as the backend's `line_two_of` writes it.
 */
export function lineTwoOf(client: string, version: string | null | undefined): string {
    return version ? versionLineOf(client, version) : "";
}

/* ------------------------------------------------------------------------- *
 * B15: how a question reads in the doc (lane 3, 2 Oct 2026)
 * ------------------------------------------------------------------------- */

/** The ask that closes each question's comment, by kind, so the client knows how to answer. */
export function questionAskOf(kind: QuestionKind, options: readonly string[]): string {
    switch (kind) {
        case "agree":
            return "Reply YES, or tell us what to change.";
        case "change":
            return "Tell us what to change.";
        case "choice":
            return options.length ? `Reply with 1 of: ${options.join(" / ")}.` : "Reply with your choice.";
        case "text":
            return "Reply with your answer.";
        case "file":
            return "Send the file in Oura under Documents, or reply with a link.";
    }
}

/** A question's own words, ending in a full stop or question mark. */
function questionSentence(text: string): string {
    // The text may carry its own leading "1." and the list and the comment both number it again.
    const t = text.replace(/\s+/g, " ").trim().replace(/^\d+[.)]\s+/, "");
    return /[.?!]$/.test(t) ? t : `${t}.`;
}

/**
 * The comment Oura posts for question `n`, exactly as it reads in the doc:
 * "[S2-MK] Q3 Are the hours the same? Reply YES, or tell us what to change."
 * The code prefix is left off when the doc's codes apply. The preview in the
 * Questions editor and the sample store both write it through here.
 */
export function questionCommentOf(q: { code: string | null; kind: QuestionKind; text: string; options: readonly string[] }, n: number): string {
    return `${q.code ? `[${q.code}] ` : ""}Q${n} ${questionSentence(q.text)} ${questionAskOf(q.kind, q.options)}`;
}

/** Line `n` of the numbered "Your questions" list Oura keeps in the doc's body: "3. [S2-MK] Are the hours the same?". */
export function questionListLineOf(q: { code: string | null; text: string }, n: number): string {
    return `${n}. ${q.code ? `[${q.code}] ` : ""}${questionSentence(q.text)}`;
}

/** The heading of that list in the doc. */
export const QUESTION_LIST_HEADING = "Your questions";

/* ------------------------------------------------------------------------- *
 * What the merchant calls a doc (Phase 1's list, and the bell from B17, B24)
 * ------------------------------------------------------------------------- */

/**
 * Doc types whose file names carry staff words ("pack", "GBP"), and what the
 * merchant reads instead. Oura's name only: the Drive file keeps its own, so
 * the file they open may still say "GBP Pack". Longest first, so "Copy pack
 * for new pages" is not read as "Copy pack". The retired names stay on the
 * list: a doc filed under the old catalogue still reads in their words.
 */
const MERCHANT_DOC_NAMES: ReadonlyArray<[RegExp, string]> = [
    [/^copy pack for new pages\b/i, "Wording for your new pages"],
    [/^monthly copy pack\b/i, "This month's page wording"],
    [/^monthly update\b/i, "This month's update"],
    [/^monthly performance report\b/i, "This month's results"],
    [/^monthly work snapshot\b/i, "This month's work"],
    [/^copy pack\b/i, "Page wording"],
    [/^(?:gbp|google profile|google business profile) changes(?: applied)?\b/i, "Google profile changes"],
    [/^(?:gbp|google profile|google business profile) pack\b/i, "Google profile"],
];

/**
 * What the merchant calls a doc, from its title: line 1, without its codes.
 * "GBP Pack Mont Kiara (S2-MK)" reads "Google profile, Mont Kiara", "Copy
 * Pack Home (B1)" reads "Page wording, Home", "Answer Plan (S1, S3, B1, G4)"
 * reads "Answer Plan". The client's list and their bell both use it.
 */
export function merchantDocName(title: string, custom = false): string {
    const line = title.split("\n")[0].replace(/\s*\((?:[A-Z]\d+(?:-[A-Z]{2,4})?[\s,]*)+\)\s*$/, "").trim() || title.trim();
    // A custom doc type is the client's own word for it (ourteam-backend#6520): its codes go, nothing
    // else changes. "GBP Photos" never becomes "Google profile Photos".
    if (custom) return line;
    for (const [staff, plain] of MERCHANT_DOC_NAMES) {
        const m = staff.exec(line);
        if (!m) continue;
        const rest = line.slice(m[0].length).replace(/^[\s,:·-]+/, "").trim();
        return rest ? `${plain}, ${rest}` : plain;
    }
    return line.replace(/\bGBP\b/g, "Google profile");
}
